import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  ClipboardCheck,
  Download,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Label } from '@/components/ui/shadcn/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/shadcn/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog';
import { useRole } from '../../../hooks/useAuth';
import { apiClient } from '../../../services/api';
import { getOverallStatusDisplay } from '../../../utils/transactionDisplayUtils';
import { cn, downloadBlob } from '../../../utils';
import type { Transaction } from '../../../types';
import {
  deriveReviewOutcome,
  formatGgusGatewayLabel,
  formatPaymentMethod,
  formatUsd,
  getOutcomeCopy,
  getSettleActionLabel,
  parseGgusApiError,
  resolvePanelAmountUsd,
  type GgusonepayManualCheckResult,
} from './ggusonepayManualReviewDisplay';

export type { GgusonepayManualCheckResult };

interface GgusonepayManualReviewPanelProps {
  transaction: Transaction;
  onSettled?: () => void;
}

function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  return fallback;
}

function toneClasses(tone: 'success' | 'warning' | 'error' | 'info'): string {
  switch (tone) {
    case 'success':
      return 'border-green-200 bg-green-50 text-green-950 dark:border-green-900/50 dark:bg-green-950/30 dark:text-green-100';
    case 'warning':
      return 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100';
    case 'error':
      return 'border-red-200 bg-red-50 text-red-950 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-100';
    default:
      return 'border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-100';
  }
}

function StepPill({
  step,
  label,
  active,
  done,
}: {
  step: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 rounded-md border px-2.5 py-2 text-xs',
        done && 'border-green-200 bg-green-50/80 dark:border-green-900/40 dark:bg-green-950/20',
        active && !done && 'border-amber-300 bg-white shadow-sm dark:bg-background',
        !active && !done && 'border-dashed bg-muted/20 text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
          done ? 'bg-green-600 text-white' : active ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground',
        )}
      >
        {done ? '✓' : step}
      </span>
      <span className="truncate font-medium">{label}</span>
    </div>
  );
}

function StatusCard({
  title,
  badge,
  amount,
  statusLabel,
  statusClassName,
  orderNo,
}: {
  title: string;
  badge: string;
  amount: string;
  statusLabel: string;
  statusClassName: string;
  orderNo?: string | null;
}) {
  return (
    <div className="rounded-lg border bg-background p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">{badge}</span>
      </div>
      <div className="space-y-2">
        <div>
          <p className="text-[11px] text-muted-foreground">Amount</p>
          <p className="text-lg font-semibold tabular-nums">{amount}</p>
        </div>
        <div>
          <p className="text-[11px] text-muted-foreground">Status</p>
          <span className={cn('mt-0.5 inline-block rounded px-2 py-0.5 text-xs font-medium', statusClassName)}>
            {statusLabel}
          </span>
        </div>
        {orderNo ? (
          <div>
            <p className="text-[11px] text-muted-foreground">Order no.</p>
            <p className="font-mono text-[11px] break-all">{orderNo}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const GgusonepayManualReviewPanel: React.FC<GgusonepayManualReviewPanelProps> = ({
  transaction,
  onSettled,
}) => {
  const canManage = useRole(['system_admin', 'super_admin']);
  const [checking, setChecking] = useState(false);
  const [settling, setSettling] = useState(false);
  const [checkResult, setCheckResult] = useState<GgusonepayManualCheckResult | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportDateFrom, setExportDateFrom] = useState(() => {
    const d = new Date(Date.now() - 90 * 86400000);
    return d.toISOString().slice(0, 10);
  });
  const [exportDateTo, setExportDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);

  const txType = transaction.type === 'withdrawal' ? 'withdrawal' : 'deposit';
  const localStatusDisplay = getOverallStatusDisplay(transaction.status);
  const outcome = deriveReviewOutcome(transaction.status, checkResult, error);
  const outcomeCopy = getOutcomeCopy(outcome, txType);
  const parsedError = error ? parseGgusApiError(error) : null;
  const settleLabel = getSettleActionLabel(txType);
  const displayAmountUsd = resolvePanelAmountUsd(transaction, checkResult);

  const showSettleSection =
    checkResult != null &&
    (checkResult.canAddCredit || checkResult.canCompletePayout) &&
    transaction.status !== 'completed';

  const playerLabel =
    transaction.recipient?.displayLabel ||
    transaction.recipient?.name ||
    transaction.depositor?.displayLabel ||
    transaction.depositor?.name ||
    null;

  const stepState = useMemo(() => {
    const checked = Boolean(checkResult || error);
    const resolved =
      transaction.status === 'completed' ||
      Boolean(checkResult?.settled) ||
      Boolean(successMessage);
    return {
      check: checked || resolved,
      review: checked,
      resolve: resolved,
    };
  }, [checkResult, error, successMessage, transaction.status]);

  if (String(transaction.provider || '').toLowerCase() !== 'ggusonepay') {
    return null;
  }

  if (transaction.type !== 'deposit' && transaction.type !== 'withdrawal') {
    return null;
  }

  if (!canManage) {
    return null;
  }

  const handleCheck = async () => {
    setChecking(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await apiClient.ggusonepayManualCheck(transaction.id);
      const data = res.data as GgusonepayManualCheckResult;
      setCheckResult(data);
      if (data.playerRefunded) {
        setSuccessMessage('Player credits were refunded because GGUS reported payout failure.');
        onSettled?.();
      } else if (data.alreadyRefunded) {
        setSuccessMessage('Player credits were already refunded for this withdrawal.');
      }
      setLastCheckedAt(new Date().toISOString());
    } catch (err: unknown) {
      setCheckResult(null);
      setError(extractApiError(err, 'Check failed'));
      setLastCheckedAt(new Date().toISOString());
    } finally {
      setChecking(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await apiClient.exportGgusonepayManualReviewLogs({
        dateFrom: exportDateFrom,
        dateTo: exportDateTo,
      });
      downloadBlob(blob, filename);
    } catch (err: unknown) {
      setExportError(extractApiError(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleSettle = async () => {
    if (!notes.trim()) {
      setError('Admin notes are required.');
      return;
    }
    setSettling(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await apiClient.ggusonepayManualSettle(transaction.id, { notes: notes.trim() });
      setCheckResult(res.data as GgusonepayManualCheckResult);
      setSuccessMessage(
        txType === 'deposit'
          ? 'Deposit credited and marked completed.'
          : 'Withdrawal marked completed.',
      );
      setNotes('');
      setConfirmOpen(false);
      onSettled?.();
    } catch (err: unknown) {
      setError(extractApiError(err, 'Settlement failed'));
      setConfirmOpen(false);
    } finally {
      setSettling(false);
    }
  };

  const ggusStatusLabel = checkResult
    ? formatGgusGatewayLabel(checkResult.gatewayState, checkResult.gatewayStateLabel)
    : 'Not checked';
  const ggusStatusClass = checkResult?.isGatewaySuccess
    ? 'bg-green-100 text-green-900'
    : outcome === 'order_not_found' || outcome === 'check_error'
      ? 'bg-red-100 text-red-900'
      : 'bg-amber-100 text-amber-900';

  return (
    <Card
      data-testid="ggus-manual-review-panel"
      className="overflow-hidden border-amber-200/80 bg-gradient-to-b from-amber-50/50 to-background dark:border-amber-900/50 dark:from-amber-950/20"
    >
      <CardHeader className="space-y-3 border-b border-amber-100/80 bg-amber-50/40 px-4 py-4 dark:border-amber-900/30 dark:bg-amber-950/10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-700 dark:text-amber-400" />
              <CardTitle className="text-sm font-semibold">GGUSOnePay manual review</CardTitle>
            </div>
            <p className="max-w-2xl text-xs text-muted-foreground">
              Compare our records with GGUS, then recover backlog transactions when the gateway reports success.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full bg-background px-2.5 py-1 text-[11px] font-medium capitalize shadow-sm">
              {txType}
            </span>
            <span className="rounded-full bg-background px-2.5 py-1 text-[11px] font-medium shadow-sm">
              {formatPaymentMethod(transaction.paymentMethod)}
            </span>
          </div>
        </div>

        <div className="grid gap-2 text-xs sm:grid-cols-3">
          <div className="rounded-md border bg-background/80 px-3 py-2">
            <p className="text-[11px] text-muted-foreground">Transaction ID</p>
            <p className="font-mono text-[11px] break-all">{transaction.transactionId}</p>
          </div>
          <div className="rounded-md border bg-background/80 px-3 py-2">
            <p className="text-[11px] text-muted-foreground">Player</p>
            <p className="truncate font-medium">{playerLabel || '—'}</p>
          </div>
          <div className="rounded-md border bg-background/80 px-3 py-2">
            <p className="text-[11px] text-muted-foreground">Amount</p>
            <p className="font-semibold tabular-nums">{formatUsd(displayAmountUsd)}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <StepPill step={1} label="Check GGUS" active={!stepState.check} done={stepState.check} />
          <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
          <StepPill step={2} label="Review" active={stepState.check && !stepState.review} done={stepState.review} />
          <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
          <StepPill step={3} label="Resolve" active={stepState.review && !stepState.resolve} done={stepState.resolve} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4 px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleCheck}
            disabled={checking || settling}
            className="min-w-[160px]"
          >
            {checking ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : checkResult || error ? (
              <RefreshCw className="mr-2 h-4 w-4" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            {checkResult || error ? 'Re-check GGUS' : 'Check GGUS status'}
          </Button>
          {lastCheckedAt ? (
            <span className="text-[11px] text-muted-foreground">
              Last checked {new Date(lastCheckedAt).toLocaleString()}
            </span>
          ) : null}
        </div>

        <div className={cn('rounded-lg border px-3 py-3 text-sm', toneClasses(outcomeCopy.tone))}>
          <div className="flex items-start gap-2">
            {outcomeCopy.tone === 'success' ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            ) : outcomeCopy.tone === 'error' ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <CircleDot className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div className="min-w-0 space-y-1">
              <p className="font-semibold">{outcomeCopy.title}</p>
              <p className="text-xs leading-relaxed opacity-90">{outcomeCopy.description}</p>
            </div>
          </div>
        </div>

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-950">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <div className="min-w-0 space-y-1">
                {parsedError?.hint ? (
                  <p className="text-xs leading-relaxed text-red-900/90">{parsedError.hint}</p>
                ) : parsedError?.title && parsedError.title !== outcomeCopy.title ? (
                  <p className="font-semibold">{parsedError.title}</p>
                ) : null}
                <p className="font-mono text-[11px] break-all text-red-800/80">{error}</p>
              </div>
            </div>
          </div>
        ) : null}

        {successMessage ? (
          <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-950">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <p>{successMessage}</p>
            </div>
          </div>
        ) : null}

        {checkResult ? (
          <div className="space-y-3">
            <div className="grid gap-3 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch">
              <StatusCard
                title="Our system"
                badge="Local"
                amount={formatUsd(displayAmountUsd)}
                statusLabel={localStatusDisplay.label}
                statusClassName={localStatusDisplay.className}
                orderNo={transaction.externalTransactionId}
              />
              <div className="hidden items-center justify-center lg:flex">
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <StatusCard
                title="GGUS gateway"
                badge="Vendor"
                amount={formatUsd(checkResult.gatewayAmountUsd)}
                statusLabel={ggusStatusLabel}
                statusClassName={ggusStatusClass}
                orderNo={checkResult.gatewayOrderNo}
              />
            </div>

            {checkResult.gatewayRealAmountUsd != null &&
            checkResult.gatewayAmountUsd != null &&
            checkResult.gatewayRealAmountUsd !== checkResult.gatewayAmountUsd ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                Real paid amount on GGUS: <strong>{formatUsd(checkResult.gatewayRealAmountUsd)}</strong>
              </div>
            ) : null}

            {checkResult.amountMismatch && checkResult.amountMismatchWarning ? (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-950">
                <div className="flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
                  <p>{checkResult.amountMismatchWarning}</p>
                </div>
              </div>
            ) : null}

            {checkResult.tierSettlementWarning ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                Tier settlement warning: {checkResult.tierSettlementWarning}
              </div>
            ) : null}

            <Collapsible open={technicalOpen} onOpenChange={setTechnicalOpen}>
              <CollapsibleTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground">
                  <ChevronDown
                    className={cn('mr-1 h-4 w-4 transition-transform', technicalOpen && 'rotate-180')}
                  />
                  Technical details
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 rounded-md border bg-muted/20 p-3 text-xs">
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Gateway state code</span>
                    <div className="font-medium">
                      {checkResult.gatewayState != null
                        ? `${checkResult.gatewayState} (${checkResult.gatewayStateLabel || 'unknown'})`
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Can settle</span>
                    <div className="font-medium">{checkResult.canSettle ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Expected USD</span>
                    <div>{formatUsd(checkResult.expectedAmountUsd)}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Gateway USD</span>
                    <div>{formatUsd(checkResult.gatewayAmountUsd)}</div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : null}

        <Collapsible open={exportOpen} onOpenChange={setExportOpen}>
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground">
              <ChevronDown className={cn('mr-1 h-4 w-4 transition-transform', exportOpen && 'rotate-180')} />
              Export report (CSV)
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-3 rounded-lg border bg-muted/20 p-3">
            <p className="text-xs text-muted-foreground">
              Download a manager-friendly CSV of all manually reviewed GGUS transactions in the selected date range.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="ggus-export-from" className="text-xs">
                  From
                </Label>
                <input
                  id="ggus-export-from"
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ggus-export-to" className="text-xs">
                  To
                </Label>
                <input
                  id="ggus-export-to"
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                />
              </div>
            </div>
            {exportError ? (
              <p role="alert" className="text-xs text-red-600">
                {exportError}
              </p>
            ) : null}
            <Button type="button" size="sm" variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Download CSV
            </Button>
          </CollapsibleContent>
        </Collapsible>

        {showSettleSection ? (
          <div className="space-y-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-4 dark:border-amber-800 dark:bg-amber-950/10">
            <div className="flex items-start gap-2">
              <ClipboardCheck className="mt-0.5 h-4 w-4 text-amber-700" />
              <div>
                <p className="text-sm font-semibold">Resolve this transaction</p>
                <p className="text-xs text-muted-foreground">
                  Add a note for audit/reporting, then confirm the action. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ggus-manual-notes" className="text-xs">
                Admin notes (required)
              </Label>
              <textarea
                id="ggus-manual-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for manual settlement, ticket ref, GGUS confirmation…"
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={settling || checking || !notes.trim()}
              onClick={() => setConfirmOpen(true)}
            >
              {settleLabel}
            </Button>
          </div>
        ) : null}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm manual resolution</DialogTitle>
            <DialogDescription>
              {txType === 'deposit'
                ? 'This will credit the player wallet and mark the deposit as completed.'
                : 'This will mark the withdrawal as completed in our system.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Transaction</span>
              <span className="font-mono text-xs">{transaction.transactionId}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-semibold">{formatUsd(displayAmountUsd)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">GGUS result</span>
              <span>{ggusStatusLabel}</span>
            </div>
            <div className="border-t pt-2">
              <p className="text-[11px] text-muted-foreground">Notes</p>
              <p className="whitespace-pre-wrap text-xs">{notes.trim()}</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)} disabled={settling}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSettle} disabled={settling}>
              {settling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm {txType === 'deposit' ? 'add credit' : 'complete payout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default GgusonepayManualReviewPanel;
